import { call, put, select, takeLatest } from 'redux-saga/effects'
import { PulsaraConstants } from '../store/pulsara.constants'
import { IPulsaraState } from '../store/pulsara.types'
import { getPulsaraSelector } from '../selectors'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { getPcrIdSelector } from '../../home/selectors'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { IPcr } from '../../pcr/store/pcr.types'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { initialPatientDemographicsState } from '../../patientDemographics/store/patientDemographics.reducer'
import { isEqual } from 'lodash'
import {
  setPatientImmunizationStatus,
  setPatientImmunizationIncompleteReason,
  setImmunizations,
} from '../../patientImmunizations/store/patientImmunizations.actions'
import {
  refreshPatientData,
  setPatientInformationState,
} from '../../patientInformation/store/patientInformation.actions'
import { refreshBillingData } from '../../billing/store/billing.actions'
import { resetPulsara } from '../store/pulsara.actions'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { resetSearchState } from '../../patientSearch/store/patientSearch.actions'
import { setSituationState } from '../../ccHpi/store/ccHpi.actions'
import { showToast } from '../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { setActivityLog } from '../../timeline/store/timeline.actions'
import { StateCustomDataConstants } from '../../nemsisData/store/nemsisCustomData.constants'
import { updateCustomGroupsFromTimeLine } from '../../nemsisData/store/nemsisCustomData.actions'
import { navigateTo } from '../../../utilities/navigation.util'
import { getTimesSelector } from '../../times/selectors'
import { resetLoader, showLoader } from '../../../components/loader/store/loader.actions'
import i18n from '../../../i18n'
import Logger from '../../../services/logger.service'
import { createTimelineItems, formatLabs, formatVitals, getPtaCutoff } from '../utils/pulsaraImportVitalsLabs.util'
import { formatAttachments } from '../utils/pulsaraImportAttachments.util'
import { IPatientVitals } from '../../vitals/store/vitals.types'

const isEmpty = (val: unknown): boolean =>
  val === undefined ||
  val === null ||
  val === '' ||
  (typeof val === 'object' && val !== null && !Array.isArray(val) && Object.keys(val).length === 0)

function omitNewEmptyKeysShallow(existing: any, merged: any): any {
  if (merged == null || typeof merged !== 'object' || Array.isArray(merged)) return merged
  const result: any = {}
  const hasExisting = existing != null && typeof existing === 'object' && !Array.isArray(existing)
  for (const key of Object.keys(merged)) {
    const existingVal = hasExisting ? existing[key] : undefined
    const mergedVal = merged[key]
    const existed = hasExisting && key in existing
    const mergedEmpty = isEmpty(mergedVal)
    if (existed || !mergedEmpty) result[key] = existed && mergedEmpty ? existingVal : mergedVal
  }
  return result
}

export function* importPulsaraData() {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)

  yield put(showLoader({ isOpen: true, message: i18n.t('pulsara.loading.importingPulsaraData') }))

  try {
    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
    const pulsaraState: IPulsaraState = yield select(getPulsaraSelector)
    const { importSelections, patientSelections } = pulsaraState
    const selectedPulsaraId = pulsaraState.selectedPulsaraId
    const { atPatientDate, atPatientTime, onSceneDate, onSceneTime } = yield select(getTimesSelector)
    const ptaCutoff = getPtaCutoff(atPatientDate, atPatientTime, onSceneDate, onSceneTime)

    const hasPulsaraCcOrPatient = Object.values(patientSelections).some((source) => source === 'pulsara')
    const existingPulsara = pcrDoc.pulsara
    const markPulsaraAsImported = hasPulsaraCcOrPatient ? true : existingPulsara?.markPulsaraAsImported ?? false
    const importedPulsaraIds =
      hasPulsaraCcOrPatient && selectedPulsaraId ? [selectedPulsaraId] : existingPulsara?.importedPulsaraIds ?? [] // maintain existing importedPulsaraIds if not importing CC or patient from new Pulsara record

    const updatedPCR: IPcr = {
      ...pcrDoc,
      ptid: !isEmpty(importSelections.ptid) ? importSelections.ptid ?? pcrDoc.ptid : pcrDoc.ptid,
      patient: { ...pcrDoc.patient, ...importSelections.patient },
      billing: { ...pcrDoc.billing, ...importSelections.billing },
      pulsara: {
        importedPulsaraIds,
        pulsaraAttachments: pcrDoc.pulsara?.pulsaraAttachments || [],
        markPulsaraAsImported,
      },
    }

    const pcrHadComplaint = pcrDoc.situation?.complaint !== undefined
    const importHasComplaint =
      importSelections.situation?.complaint != null && importSelections.situation.complaint !== ''
    if (pcrHadComplaint || importHasComplaint) {
      updatedPCR.situation = {
        ...pcrDoc.situation,
        ...importSelections.situation,
      }
      yield put(setSituationState(updatedPCR.situation))
    }

    updatedPCR.patient = omitNewEmptyKeysShallow(pcrDoc.patient, updatedPCR.patient)
    updatedPCR.billing = omitNewEmptyKeysShallow(pcrDoc.billing, updatedPCR.billing)
    const pcrRecord = updatedPCR as unknown as Record<string, unknown>
    if (!('ptid' in pcrDoc) && updatedPCR.ptid === undefined) delete pcrRecord.ptid
    if (pcrDoc.billing == null && isEmpty(updatedPCR.billing)) delete pcrRecord.billing

    const hadPatientDemographics =
      pcrDoc.patient?.patientDemographics != null && Object.keys(pcrDoc.patient.patientDemographics).length > 0
    if (
      !hadPatientDemographics &&
      updatedPCR.patient.patientDemographics != null &&
      isEqual(initialPatientDemographicsState, updatedPCR.patient.patientDemographics)
    ) {
      const { patientDemographics: _removed, ...restPatient } = updatedPCR.patient
      updatedPCR.patient = { ...restPatient } as typeof updatedPCR.patient
    }

    const hasPtDemographics = !!updatedPCR.patient.patientDemographics
    const hasPtMedicalHistory = !!updatedPCR.patient.patientMedicalHistory
    const hasPtAllergies = !!updatedPCR.patient.patientAllergies
    const hasPtMedications = !!updatedPCR.patient.patientMedications
    const hasPtImmunizations = !!updatedPCR.patient.patientImmunizations
    const hasInsuranceList = (updatedPCR.billing?.insuranceInformation?.insuranceInformationList?.length ?? 0) > 0
    const hasRelationshipsList =
      (updatedPCR.billing?.relationshipsAndGuarantors?.relationshipsAndGuarantorsList?.length ?? 0) > 0
    const hasPtid = !isEmpty(updatedPCR.ptid)
    const hasPatient =
      hasPtDemographics ||
      hasPtMedicalHistory ||
      hasPtAllergies ||
      hasPtMedications ||
      hasPtImmunizations ||
      hasInsuranceList ||
      hasRelationshipsList ||
      hasPtid
    updatedPCR.patient.patientInformation.hasPatient = hasPatient
    yield put(setPatientInformationState(updatedPCR.patient.patientInformation))

    const isPatientDemographicsChanged = !isEqual(
      initialPatientDemographicsState,
      updatedPCR.patient.patientDemographics
    )

    if (isPatientDemographicsChanged) {
      if (updatedPCR.patient.patientDemographics?.immunizationStatus) {
        yield put(
          setPatientImmunizationStatus(
            updatedPCR.patient.patientDemographics?.immunizationStatus,
            updatedPCR.patient.patientDemographics?.immunizationStatusDate
          )
        )
      }
      if (updatedPCR.patient.patientDemographics?.immunizationIncompleteReason) {
        yield put(
          setPatientImmunizationIncompleteReason(updatedPCR.patient.patientDemographics?.immunizationIncompleteReason)
        )
      }
      if (updatedPCR.patient.patientImmunizations?.immunizations) {
        yield put(setImmunizations(updatedPCR.patient.patientImmunizations?.immunizations))
      }
    }

    const existingTimelineItems = updatedPCR.patient.timelineItems || []
    const existingPatientVitals = updatedPCR.patient.patientVitals || []
    const existingLabsStateList = updatedPCR.patient.labsStateList || []
    const existingPulsaraAttachments = updatedPCR.pulsara?.pulsaraAttachments || []

    const timelineItems = [...existingTimelineItems]

    if (importSelections?.selectedVitals?.length) {
      const vitalsNotAlreadyOnPcr = importSelections.selectedVitals.filter(
        (vital: { key?: string }) =>
          !existingTimelineItems.some((t) => String(t.key) === String(vital.key)) &&
          !existingPatientVitals.some((v: { key?: string }) => String(v.key) === String(vital.key))
      )
      if (vitalsNotAlreadyOnPcr?.length) {
        const formattedVitals = formatVitals(vitalsNotAlreadyOnPcr, ptaCutoff)
        updatedPCR.patient.patientVitals = [
          ...(updatedPCR.patient.patientVitals || []),
          ...formattedVitals,
        ] as IPatientVitals[]
        timelineItems.push(...createTimelineItems(formattedVitals, 'Vitals', ptaCutoff))
      }
    }

    if (importSelections?.selectedLabs?.length) {
      const labsNotAlreadyOnPcr = importSelections.selectedLabs.filter(
        (lab: { key?: string }) =>
          !existingTimelineItems.some((t) => String(t.key) === String(lab.key)) &&
          !existingLabsStateList.some((l: { key?: string }) => String(l.key) === String(lab.key))
      )
      if (labsNotAlreadyOnPcr?.length) {
        const formattedLabs = formatLabs(labsNotAlreadyOnPcr, ptaCutoff)
        updatedPCR.patient.labsStateList = [...(updatedPCR.patient.labsStateList || []), ...formattedLabs]
        timelineItems.push(...createTimelineItems(formattedLabs, 'Labs', ptaCutoff))
      }
    }

    if (timelineItems?.length > 0) {
      timelineItems.sort((a, b) => {
        return +new Date(a.date) - +new Date(b.date)
      })
      updatedPCR.patient.timelineItems = timelineItems
      yield put(setActivityLog(timelineItems))
    }

    if (importSelections?.selectedAttachments?.length) {
      const attachmentsNotAlreadyOnPcr = importSelections.selectedAttachments.filter(
        (att: { id?: number }) => !existingPulsaraAttachments.some((a: { id?: number }) => a.id === att.id)
      )
      if (attachmentsNotAlreadyOnPcr?.length) {
        const pulsaraAttachmentsForPcr = formatAttachments(attachmentsNotAlreadyOnPcr)
        updatedPCR.pulsara.pulsaraAttachments = [...updatedPCR.pulsara.pulsaraAttachments, ...pulsaraAttachmentsForPcr]
      }
    }

    yield call([pcrDocsService, pcrDocsService.savePCR], updatedPCR)
    yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.ADD_TIMELINE_GROUP))

    yield put(refreshPatientData())
    yield put(refreshBillingData())

    yield put(resetPulsara())
    yield put(resetSearchState())

    yield put(
      showToast({
        message: 'Pulsara data imported successfully',
        position: 'top',
        color: ToastColor.Success,
      })
    )

    Logger.info('Pulsara: importPulsaraData completed successfully')

    navigateTo(TabConstants.CHART_ROUTE)
  } catch (e) {
    yield put(throwPcrException(`importPulsaraData() error: ${e}`))
  } finally {
    yield put(resetLoader())
  }
}

export function* importPulsaraDataSaga() {
  yield takeLatest(PulsaraConstants.IMPORT_PULSARA_DATA, performWithMutexHOG, importPulsaraData)
}
